import {
  infiniteQueryOptions,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";

import { api, BASE_API_URL_V3 } from "@/api/api";
import type {
  FlightSearchInfiniteQueryParams,
  FlightSearchParams,
  SearchParams,
} from "@/app/lib/hooks/flightSearch/types";
import type { FindFlightOffersOutputDtoV3 } from "@/types/codegen";
import type { IFlightFilters } from "@/types/interfaces";
import type {
  FlightSearchType,
  FlightsSortValue,
  TFlightFormValues,
} from "@/types/types";

import { useFlightSearchDataContext } from "../../context/FlightSearchDataContext";
import {
  useFlightSelectionPayloadContext,
  type FlightType,
  type FullOfferType,
} from "../../context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useFlightSortContext } from "../../context/FlightSortContext";
import { getFilterValue, getPricesFilterValues } from "../../utils/filters";
import { PAGE_SIZE } from "../useFlightSearchTokens";

const createSearchParams = (
  data: FlightSearchParams["data"],
  filters: IFlightFilters | null,
  sortValue: FlightsSortValue,
  searchData: TFlightFormValues | null,
): SearchParams | null => {
  if (!data?.searchResultKey || !data?.searchResultToken) {
    return null;
  }

  return {
    searchResultKey: data.searchResultKey,
    searchResultToken: data.searchResultToken,
    perPage: PAGE_SIZE,
    sortOrder: sortValue.order,
    sortField: sortValue.field,
    ...getPricesFilterValues(
      filters?.priceRange,
      data.defaultFilters.priceRange,
    ),
    carriers: getFilterValue(
      filters?.selectedAirlines,
      data.defaultFilters.selectedAirlines,
    ),
    stops: filters?.stops || data.defaultFilters.stops,
    ...(searchData
      ? {
          destinationCity:
            searchData.to.type === "CITY" ? searchData.to.name : undefined,
          destinationCountry: searchData.to.countryName,
        }
      : {}),
  };
};

export const searchFlightsQueryOptions = <TData>(
  params: SearchParams | null,
  searchType: FlightSearchType | null,
) =>
  infiniteQueryOptions<
    FlightSearchInfiniteQueryParams<TData>["TQueryFnData"],
    FlightSearchInfiniteQueryParams<TData>["TError"],
    FlightSearchInfiniteQueryParams<TData>["TData"],
    FlightSearchInfiniteQueryParams<TData>["TQueryKey"],
    FlightSearchInfiniteQueryParams<TData>["TPageParam"]
  >({
    queryKey: ["flights", "search", "results", searchType, params] as const,
    queryFn: async ({ pageParam, queryKey: [, , , , params], signal }) => {
      const response = await api
        .get<FindFlightOffersOutputDtoV3>("/flights", {
          params: { ...params, page: pageParam },
          signal,
          baseURL: BASE_API_URL_V3,
        })
        .then((res) => res.data.data);

      if (!params?.searchResultToken)
        throw new Error("Missing search result token");

      const fullOffers: FullOfferType[] = response.offers.map((offer) => {
        if (offer.type === "PACKAGE_BANNER") {
          return offer;
        }

        return {
          ...offer,
          searchResultToken: params.searchResultToken,
          flightsOverview: offer.flightsOverview.map((fo) => {
            const [departureFlightRef, returnFlightRef] = fo.flightReferences;

            const departureFlight = response.flightList.find(
              (flight) => flight.key === departureFlightRef,
            );
            const returnFlight = response.flightList.find(
              (flight) => flight.key === returnFlightRef,
            );

            if (!departureFlight) throw new Error();

            const departureFlightWithSegments: FlightType = {
              ...departureFlight,
              segments: departureFlight.segmentReferences
                .map((referenceKey) =>
                  response.flightSegmentList.find(
                    (segment) => segment.key === referenceKey,
                  ),
                )
                .filter((x): x is NonNullable<typeof x> => Boolean(x)),
            };

            const returnFlightWithSegments: FlightType | undefined =
              returnFlight
                ? {
                    ...returnFlight,
                    segments: returnFlight.segmentReferences
                      .map((referenceKey) =>
                        response.flightSegmentList.find(
                          (segment) => segment.key === referenceKey,
                        ),
                      )
                      .filter((x): x is NonNullable<typeof x> => Boolean(x)),
                  }
                : undefined;

            return {
              ...fo,
              departureFlight: departureFlightWithSegments,
              returnFlight: returnFlightWithSegments,
            };
          }),
        };
      });

      return { offers: fullOffers, hasMore: response.hasMore };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.hasMore ? pages.length + 1 : undefined;
    },
    enabled: Boolean(params),
  });

export const useFlightSearch = <TData = FullOfferType[]>(
  {
    data,
    isError: isTokenError,
    isLoading: isTokenLoading,
    isFetching: isTokenFetching,
  }: FlightSearchParams,
  filters: IFlightFilters | null,
  options?: Pick<
    UseInfiniteQueryOptions<
      FlightSearchInfiniteQueryParams<TData>["TQueryFnData"],
      FlightSearchInfiniteQueryParams<TData>["TError"],
      FlightSearchInfiniteQueryParams<TData>["TData"],
      FlightSearchInfiniteQueryParams<TData>["TQueryKey"],
      FlightSearchInfiniteQueryParams<TData>["TPageParam"]
    >,
    "select"
  >,
  searchType: FlightSearchType | null = "full",
) => {
  const { sortValue } = useFlightSortContext();
  const { searchData } = useFlightSearchDataContext();
  const { selectedFlightSearchSource } = useFlightSelectionPayloadContext();

  const params = createSearchParams(
    data,
    filters,
    sortValue,
    searchType === "full" ? searchData : null,
  );

  const {
    data: result,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    isSuccess,
  } = useInfiniteQuery<
    FlightSearchInfiniteQueryParams<TData>["TQueryFnData"],
    FlightSearchInfiniteQueryParams<TData>["TError"],
    FlightSearchInfiniteQueryParams<TData>["TData"],
    FlightSearchInfiniteQueryParams<TData>["TQueryKey"],
    FlightSearchInfiniteQueryParams<TData>["TPageParam"]
  >({
    ...searchFlightsQueryOptions<TData>(
      params,
      searchType ?? selectedFlightSearchSource,
    ),
    ...options,
  });

  return {
    result,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoading || isTokenLoading,
    isFetching: isFetching || isTokenFetching,
    isError: isError || isTokenError,
    searchParams: params,
    isSuccess,
  };
};
